import { Suspense } from "react";

import { css } from "@compiled/react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql, VariablesOf } from "relay-runtime";

import { media } from "@atlaskit/primitives/responsive";

import { confluenceTemplateCollectionDetailsRelayPageQuery } from "@/__generated__/confluenceTemplateCollectionDetailsRelayPageQuery.graphql";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { expectContent } from "@/content/utils/expectContent";

import { SharedHeaderFooterContainer } from "../sections/shared-header-footer-container";

import { parseQueryLocale } from "./hooks/use-query-locale";
import { CollectionAboutSection } from "./sections/collection-about-section-relay";
import { CollectionGuideSection } from "./sections/collection-guide-section-relay";
import { CollectionHeaderSection } from "./sections/collection-header-section-relay";
import { CollectionRelatedSection } from "./sections/collection-related-section-relay";

export type ConfluenceTemplateCollectionDetailsRelayPageTemplateProps = {
    queryRef: PreloadedQuery<confluenceTemplateCollectionDetailsRelayPageQuery>;
};

const contentWrapper = css({
    "maxWidth": 1045,
    "margin": "0 auto",

    "@media (min-width: 30rem) and (max-width: 67.49rem)": {
        paddingInlineStart: 64,
        paddingInlineEnd: 64
    },
    [media.below.xs]: {
        paddingInlineStart: 32,
        paddingInlineEnd: 32
    }
});

const parseCollectionUrlSlug = (pathname: string) => {
    const [urlSlug, maybeCollectionsSlug] = pathname.split("/").reverse();
    return urlSlug && maybeCollectionsSlug === "collections" ? urlSlug : "";
};

const RELATED_COLLECTION_URL_SLUGS: Record<string, string[]> = {
    ["project-management"]: ["product-managers", "strategic-planning"],
    ["strategic-planning"]: ["project-management", "product-managers"]
};

const DEFAULT_RELATED_COLLECTION_URL_SLUGS = [
    "project-management",
    "strategic-planning"
];

export const ConfluenceTemplateCollectionDetailsRelayPageQuery = graphql`
    query confluenceTemplateCollectionDetailsRelayPageQuery(
        $locale: String!
        $urlSlug: String!
        $relatedCollectionUrlSlugs: [String!]!
    ) {
        collections: templateCollections(
            search: {
                queries: {
                    first: 1
                    withLocales: [$locale]
                    where: { fieldNamed: "urlSlug", havingValues: [$urlSlug] }
                }
            }
        ) {
            edges {
                node {
                    ...collectionHeaderSectionRelayFragment
                    ...collectionAboutSectionRelayFragment
                    howToUseThisCollection {
                        ...collectionGuideSectionRelayFragment
                    }
                }
            }
        }
        relatedCollections: templateCollections(
            search: {
                queries: {
                    first: 2
                    withLocales: [$locale]
                    where: {
                        fieldNamed: "urlSlug"
                        havingValues: $relatedCollectionUrlSlugs
                    }
                }
            }
        ) {
            ...collectionRelatedSectionRelayFragment
        }
    }
`;

export const getConfluenceTemplateCollectionDetailsRelayPageQueryVars = (
    location: Location,
    partialVars?: Partial<
        VariablesOf<confluenceTemplateCollectionDetailsRelayPageQuery>
    >
): VariablesOf<confluenceTemplateCollectionDetailsRelayPageQuery> => {
    const locale = partialVars?.locale ?? parseQueryLocale(location);

    const urlSlug =
        partialVars?.urlSlug ?? parseCollectionUrlSlug(location.pathname);

    const relatedCollectionUrlSlugs =
        partialVars?.relatedCollectionUrlSlugs ??
        RELATED_COLLECTION_URL_SLUGS[urlSlug] ??
        DEFAULT_RELATED_COLLECTION_URL_SLUGS;

    return { locale, urlSlug, relatedCollectionUrlSlugs };
};

type CollectionDetailsContentRelayProps = {
    queryRef: PreloadedQuery<confluenceTemplateCollectionDetailsRelayPageQuery>;
};
const CollectionDetailsContentRelay = ({
    queryRef
}: CollectionDetailsContentRelayProps) => {
    const { collections, relatedCollections } =
        usePreloadedQuery<confluenceTemplateCollectionDetailsRelayPageQuery>(
            ConfluenceTemplateCollectionDetailsRelayPageQuery,
            queryRef
        );

    const collection = expectContent(collections.edges[0]?.node);

    return (
        <div data-testid="confluence-template-collection-details-content">
            <CollectionHeaderSection collection={collection} />
            <div css={contentWrapper}>
                <CollectionAboutSection collection={collection} />
                {collection.howToUseThisCollection && (
                    <CollectionGuideSection
                        collectionGuide={collection.howToUseThisCollection}
                    />
                )}
                <CollectionRelatedSection collections={relatedCollections} />
            </div>
        </div>
    );
};

export const ConfluenceTemplateCollectionDetailsRelayPageTemplate = ({
    queryRef
}: ConfluenceTemplateCollectionDetailsRelayPageTemplateProps) => {
    return (
        <SharedHeaderFooterContainer>
            <ContentNotFoundErrorBoundary>
                <Suspense>
                    <CollectionDetailsContentRelay queryRef={queryRef} />
                </Suspense>
            </ContentNotFoundErrorBoundary>
        </SharedHeaderFooterContainer>
    );
};
